import type {
  BillingContext,
  BusinessFrontendMode,
} from "@smartwiz/shared-types";

export type { BillingContext };

export interface ResendEmailPayload {
  email?: string;
  token?: string;
}

// Types for the login response
export type Role = "owner" | "team_member" | "team_lead" | "client";

export interface UserAuthData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: Role;
  businessId: string;
  businessName: string;
  phoneNumber?: string; // User's phone number
  authMethod?: "app" | "google"; // Authentication method: 'app' for regular login, 'google' for Google OAuth
  requiresPaymentUpdate?: boolean; // True if user needs to update payment method
  paymentFailedReason?: string; // Reason for payment failure (e.g., "Card declined")
  useLiteHome?: boolean; // If true, redirect to /home/lite instead of /home on login
  isActive?: boolean; // True if subscription is active
  isTrialActive?: boolean; // True if subscription is in trial period
  hadSubscription?: boolean; // True if user ever had a subscription (for expired vs new user flow)
  /** Server: owner should see contract upgrade campaign (subject to feature flag + cookie). */
  needsContractUpgrade?: boolean;
  /** Service bureau profile present (from /token/check and /token/me user payload). */
  isServiceBureau?: boolean;
  frontendMode?: BusinessFrontendMode;
  legacyFrontendSunsetAt?: string | null;
  /** Intake migration rollout (enrollment, campaign UI, login redirects). */
  intakeFrontendMigrationEnabled?: boolean;
  /** True when the billing owner has an active contract for intake migration. */
  intakeMigrationEligible?: boolean;
  /** @deprecated Alias of intakeFrontendMigrationEnabled on older auth payloads. */
  intakeFrontendRedirectsEnabled?: boolean;
  /** Effective capability keys for UI gating (server resolves bundles + grants). */
  serviceBureauCapabilities?: string[];
  managedBusinessCount?: number;
  defaultPortal?: string | null;
}

export interface RequiresAction {
  subscriptionActivation?: boolean;
  billingAccountIssue?: boolean;
  paymentUpdate?: boolean;
  /** @deprecated Prefer paymentUpdate (camelCase from API). */
  paymentUpdated?: boolean;
  emailVerification?: boolean;
}

export interface ActionMessages {
  subscriptionActivation?: string;
  billingAccountIssue?: string;
  paymentUpdated?: string;
  emailVerification?: string;
}

export interface BusinessOption {
  businessId: string;
  businessName: string;
  slug: string;
  allowed: boolean;
}

export interface BusinessSelectionContent {
  status: "business_selection_required";
  businesses: BusinessOption[];
}

export interface BusinessSelectionResponse {
  message: string;
  content: BusinessSelectionContent;
}

export interface LoginResponse {
  content: {
    user: UserAuthData;
    requiresAction?: RequiresAction;
    message?: ActionMessages | string;
    billingContext?: BillingContext;
  };
}

/** Response from the login API — may be a normal login or a business selection prompt. */
export type LoginApiResponse = LoginResponse | BusinessSelectionResponse;

export function isBusinessSelectionResponse(
  response: LoginApiResponse
): response is BusinessSelectionResponse {
  return (
    "status" in response.content &&
    response.content.status === "business_selection_required"
  );
}

// Type for error login state
export interface LoginErrorUserInfo {
  id: number;
  email: string;
  role?: string;
  business_id?: string | null;
}

export interface LoginErrorResponseBase {
  message: string;
  content: {
    error: string;
    details: string;
    user?: LoginErrorUserInfo;
  };
}

export type LoginErrorResponse =
  | {
      message: string;
      content: {
        error: "credentials_required";
        details: "Both email and password are required for login";
      };
    }
  | {
      message: string;
      content: {
        error: "invalid_credentials";
        details: "The provided email or password is incorrect";
      };
    }
  | {
      message: string;
      content: {
        error: "user_archived";
        details: string;
        user: LoginErrorUserInfo;
      };
    }
  | {
      message: string;
      content: {
        error: "emailNotVerified";
        details: string;
        user: LoginErrorUserInfo;
      };
    }
  | {
      message: string;
      content: {
        error: "roleNotFound";
        details: string;
        user: LoginErrorUserInfo;
      };
    }
  | {
      message: string;
      content: {
        error: "business_access_denied";
        details: string;
        url: string;
      };
    }
  | {
      message: string;
      content: {
        error: "not_authorized";
        details: string;
        url: string;
      };
    };
